import type { PodUser, PodUserDeliveryAddressUpdate } from '@mathflat/domain/PodOrder'
import {
  DELIVERY_ADDRESS_MAX,
  type DeliveryAddress,
  type DeliveryAddressDraft,
  type DeliveryAddressId,
  type RecentRecipient,
} from '@mathflat/domain/PodOrder'
import { makeAutoObservable } from 'mobx'

import { podMallController } from '@/controller/podMall.controller'

import type { PreOrderFormData } from '../../../PODMallOrder/_service/PreOrderParams'
import { ApiDeliveryAddressRepository } from '../_repository/ApiDeliveryAddressRepository'
import type { DeliveryAddressRepository } from '../_repository/DeliveryAddressRepository'

type DeliveryFormValues = Pick<
  PreOrderFormData,
  | 'deliveryRecipientName'
  | 'deliveryRecipientPhoneNumber'
  | 'deliveryRecipientEmail'
  | 'deliveryAddress'
  | 'deliveryAddressDetail'
  | 'deliveryZipCode'
  | 'deliveryMemo'
>

export type AddressBookStatus = 'idle' | 'loading' | 'loaded' | 'error'

const normalize = (value: string | null) => (value ?? '').trim()

const isSameAddress = (
  a: Pick<DeliveryAddress, 'zipCode' | 'address' | 'addressDetail'>,
  b: Pick<DeliveryAddress, 'zipCode' | 'address' | 'addressDetail'>,
) =>
  normalize(a.zipCode) === normalize(b.zipCode) &&
  normalize(a.address) === normalize(b.address) &&
  normalize(a.addressDetail) === normalize(b.addressDetail)

class PODMallUserService {
  user: PodUser | null = null

  /** 학원(D코드) 주소록. 서버 표시 순서 그대로(최근 주문 배송지가 맨 위). */
  addressBook: DeliveryAddress[] = []
  recentRecipient: RecentRecipient | null = null
  addressBookStatus: AddressBookStatus = 'idle'

  private _addressRepo: DeliveryAddressRepository
  private _addressBookRequestId = 0

  constructor(addressRepo: DeliveryAddressRepository = new ApiDeliveryAddressRepository()) {
    this._addressRepo = addressRepo
    makeAutoObservable<this, '_addressRepo' | '_addressBookRequestId'>(this, {
      _addressRepo: false,
      _addressBookRequestId: false,
    })
  }

  /** 수정·삭제할 수 있는 항목. `id: null`(주소록 밖 최근 주문 배송지)은 제외 */
  get manageableAddresses() {
    return this.addressBook.filter((item) => item.id !== null)
  }

  /** 주문서에서 고를 수 있는 항목. 주소가 비어 내려온 항목은 결제 검증을 못 넘기므로 제외 */
  get selectableAddresses() {
    return this.addressBook.filter((item) => !!item.address)
  }

  get recentAddress() {
    return this.selectableAddresses.find((item) => item.isRecentOrder) ?? null
  }

  get canAddAddress() {
    return this.manageableAddresses.length < DELIVERY_ADDRESS_MAX
  }

  get canRemoveAddress() {
    return this.manageableAddresses.length > 1
  }

  // 기본 배송지를 아직 정하지 않은 칸은 서버가 null 로 준다. 입력 칸은 빈 문자열로 채운다
  get deliveryFormValues(): DeliveryFormValues | undefined {
    if (!this.user) return undefined
    const { user } = this
    return {
      deliveryRecipientName: user.deliveryRecipientName,
      deliveryRecipientPhoneNumber: user.deliveryRecipientPhoneNumber ?? '',
      deliveryRecipientEmail: user.deliveryRecipientEmail ?? '',
      deliveryAddress: user.deliveryAddress ?? '',
      deliveryAddressDetail: user.deliveryAddressDetail ?? '',
      deliveryZipCode: user.deliveryZipCode ?? '',
      deliveryMemo: user.deliveryMemo ?? '',
    }
  }

  async fetchSetUser() {
    this.user = await podMallController.getUser()
  }

  async postUserDeliveryAddress(params: PreOrderFormData) {
    const payload: PodUserDeliveryAddressUpdate = {
      deliveryRecipientName: params.deliveryRecipientName,
      deliveryRecipientPhoneNumber: params.deliveryRecipientPhoneNumber,
      deliveryRecipientEmail: params.deliveryRecipientEmail,
      deliveryAddress: params.deliveryAddress,
      deliveryAddressDetail: params.deliveryAddressDetail,
      deliveryZipCode: params.deliveryZipCode,
      deliveryMemo: params.deliveryMemo,
    }
    if (this.user) {
      this.user = { ...this.user, ...payload, deliveryRecipientName: params.deliveryRecipientName }
    }
    await podMallController.postUserDeliveryAddress(payload)
  }

  /** 배송정보 변경 탭과 주문서가 같은 인스턴스로 조회하므로, 겹치면 마지막 요청의 응답만 반영한다. */
  async fetchAddressBook() {
    const requestId = ++this._addressBookRequestId
    this.addressBookStatus = 'loading'
    try {
      const { recentRecipient, addresses } = await this._addressRepo.list()
      if (requestId !== this._addressBookRequestId) return
      this.addressBook = addresses
      this.recentRecipient = recentRecipient
      this.addressBookStatus = 'loaded'
    } catch {
      if (requestId !== this._addressBookRequestId) return
      this.addressBook = []
      this.recentRecipient = null
      this.addressBookStatus = 'error'
    }
  }

  /**
   * 단건 응답은 `isRecentOrder` 가 늘 false 이고, 최근 배송지 판정(주소 일치·id 없는 직전 주문 항목)은 목록 조회에서만 정해진다.
   * 그래서 쓰기 뒤에는 받은 응답으로 바로 보여 주고 목록을 다시 받아 서버 판정으로 맞춘다.
   */
  async createAddress(draft: DeliveryAddressDraft) {
    const created = await this._addressRepo.create(draft)
    this.addressBook = [...this.addressBook, created]
    this.fetchAddressBook()
    return created
  }

  async updateAddress(id: DeliveryAddressId, draft: DeliveryAddressDraft) {
    const updated = await this._addressRepo.update(id, draft)
    this.addressBook = this.addressBook.map((item) => (item.id === id ? updated : item))
    this.fetchAddressBook()
    return updated
  }

  async removeAddress(id: DeliveryAddressId) {
    await this._addressRepo.remove(id)
    this.addressBook = this.addressBook.filter((item) => item.id !== id)
    this.fetchAddressBook()
  }

  /** 서버에 중복 판정이 없어 받아 둔 목록으로 판정한다(우편번호+주소+상세주소, 앞뒤 공백 무시). */
  isDuplicateAddress(draft: DeliveryAddressDraft, excludeId?: DeliveryAddressId) {
    return this.manageableAddresses.some(
      (item) => item.id !== excludeId && isSameAddress(item, draft),
    )
  }

  /**
   * 주소록 모드 주문서 초기값. preOrder 주소는 레거시 기본 배송지라 최근 배송지(없으면 첫 항목, 주소록이 비면 빈 값)로 덮고,
   * 빈 수령인 칸은 직전 주문 수령인으로 채운다.
   * `기본 배송지로 저장` 은 화면에 없으므로 preOrder 가 주소를 들고 와 파생된 true 를 false 로 되돌린다.
   */
  toAddressBookOrderForm(form: PreOrderFormData): PreOrderFormData {
    const recipient = this.recentRecipient
    const initial = this.recentAddress ?? this.selectableAddresses[0]
    return {
      ...form,
      deliveryZipCode: initial?.zipCode ?? '',
      deliveryAddress: initial?.address ?? '',
      deliveryAddressDetail: initial?.addressDetail ?? '',
      defaultAddressSavingFlag: false,
      deliveryRecipientName: form.deliveryRecipientName || (recipient?.name ?? ''),
      deliveryRecipientPhoneNumber:
        form.deliveryRecipientPhoneNumber || (recipient?.phoneNumber ?? ''),
      deliveryRecipientEmail: form.deliveryRecipientEmail || (recipient?.email ?? ''),
    }
  }
}

export const podMallUserService = new PODMallUserService()
export { PODMallUserService }
